import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import QRCode from 'qrcode';
import { ArrowRight, Lock } from 'lucide-react';
import { ADMIN_PASSWORD_HELP, getAdminPasswordError } from '../utils/adminPasswordPolicy';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('credentials');
  const [mfaCode, setMfaCode] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const { login, verifyMfa } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/admin';

  useEffect(() => {
    let cancelled = false;
    if (!otpauthUrl || stage !== 'setup') {
      setQrCodeDataUrl('');
      return undefined;
    }

    QRCode.toDataURL(otpauthUrl, { margin: 1, width: 220 })
      .then((dataUrl) => {
        if (!cancelled) setQrCodeDataUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setError('Impossibile generare il QR code. Usa il segreto mostrato qui sotto per la configurazione manuale.');
      });

    return () => {
      cancelled = true;
    };
  }, [otpauthUrl, stage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      setPasswordChangeRequired(Boolean(result.passwordChangeRequired));
      setPassword('');
      if (result.admin) {
        navigate(from, { replace: true });
      } else if (result.mfaSetupRequired) {
        setOtpauthUrl(result.otpauthUrl);
        setManualSecret(new URL(result.otpauthUrl).searchParams.get('secret') || '');
        setStage(result.passwordChangeRequired ? 'password' : 'setup');
      } else if (result.mfaRequired) {
        setOtpauthUrl('');
        setStage(result.passwordChangeRequired ? 'password' : 'verify');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  const validateNewPassword = () => {
    const passwordError = getAdminPasswordError(newPassword);
    if (passwordError) {
      setError(passwordError);
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('Le nuove password non coincidono.');
      return false;
    }
    return true;
  };

  const handlePasswordContinue = (e) => {
    e.preventDefault();
    setError('');
    if (validateNewPassword()) {
      setStage(otpauthUrl ? 'setup' : 'verify');
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (passwordChangeRequired && !validateNewPassword()) {
      setStage('password');
      return;
    }
    setLoading(true);
    try {
      const result = await verifyMfa(mfaCode, passwordChangeRequired ? newPassword : undefined);
      setNewPassword('');
      setConfirmPassword('');
      setMfaCode('');
      if (result.recoveryCodes?.length) {
        setRecoveryCodes(result.recoveryCodes);
        setStage('recovery');
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Codice di verifica non valido');
    } finally {
      setLoading(false);
    }
  };

  const handleRecoveryContinue = () => navigate(from, { replace: true });

  const isMfaStep = stage === 'setup' || stage === 'verify';
  const steps = passwordChangeRequired ? ['Accesso', 'Nuova password', '2FA'] : ['Accesso', '2FA'];
  const stepIndex = stage === 'credentials' ? 0 : stage === 'password' ? 1 : steps.length - 1;
  const stageCopy = {
    credentials: {},
    password: {
      title: 'Imposta la tua password',
      description: 'Sostituisci la password iniziale. Verrà salvata dopo la verifica 2FA nel prossimo passaggio.'
    },
    setup: {
      eyebrow: 'CONFIGURAZIONE INIZIALE',
      title: 'Proteggi il tuo account',
      description: 'Collega un’app autenticatrice per completare la configurazione.'
    },
    verify: {
      eyebrow: 'VERIFICA IDENTITÀ',
      title: 'Conferma che sei tu',
      description: 'Inserisci il codice temporaneo generato dalla tua app autenticatrice.'
    },
    recovery: {
      eyebrow: 'CONFIGURAZIONE COMPLETATA',
      title: 'Salva i codici di recupero',
      description: 'Ti serviranno per accedere se non puoi usare l’app autenticatrice.'
    }
  }[stage];

  return (
    <main className="login-page">
      <div className="login-glow login-glow-one" />
      <div className="login-glow login-glow-two" />
      <div className="login-shell">
        <section className="login-main-panel">
          <div className="login-card">
            <div className="login-card-brand">
              <img src="/soontip_logo_color.svg" alt="SoonTip" />
              <small>Pannello amministrativo</small>
            </div>
            {stageCopy.eyebrow && <p className="login-eyebrow">{stageCopy.eyebrow}</p>}
            {stageCopy.title && <h2>{stageCopy.title}</h2>}
            {stageCopy.description && <p className="login-intro">{stageCopy.description}</p>}

            {stage !== 'recovery' && (
              <div className="login-progress" role="group" aria-label={`Passaggio ${stepIndex + 1} di ${steps.length}: ${steps[stepIndex]}`}>
                <p className="login-progress-label">
                  {stage === 'credentials' ? 'Accesso' : `Passaggio ${stepIndex + 1} di ${steps.length} · ${steps[stepIndex]}`}
                </p>
                <div className="login-stage-indicator" aria-hidden="true">
                  {steps.map((step, index) => (
                    <span key={step} className={index === stepIndex ? 'active' : index < stepIndex ? 'complete' : ''} />
                  ))}
                </div>
              </div>
            )}

            {error && <div className="error-message" role="alert">{error}</div>}

            {stage === 'credentials' && (
              <form onSubmit={handleSubmit} className="login-form">
                <div className="form-group">
                  <label htmlFor="email">Indirizzo email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@azienda.it"
                    required
                    autoComplete="username"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Inserisci la tua password"
                    required
                    autoComplete="current-password"
                    disabled={loading}
                  />
                </div>
                <button type="submit" className="login-button" disabled={loading}>
                  <span>{loading ? 'Verifica in corso...' : 'Accedi'}</span>
                  {!loading && <ArrowRight size={18} />}
                </button>
              </form>
            )}

            {stage === 'password' && (
              <form onSubmit={handlePasswordContinue} className="login-form">
                <div className="form-group">
                  <label htmlFor="first-login-password">Nuova password</label>
                  <input
                    id="first-login-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    maxLength={128}
                    aria-describedby="first-login-password-help"
                  />
                  <small id="first-login-password-help" className="login-password-help">{ADMIN_PASSWORD_HELP}</small>
                </div>
                <div className="form-group">
                  <label htmlFor="first-login-password-confirm">Conferma nuova password</label>
                  <input
                    id="first-login-password-confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    maxLength={128}
                  />
                </div>
                <button type="submit" className="login-button">
                  <span>Continua</span><ArrowRight size={18} />
                </button>
              </form>
            )}

            {isMfaStep && (
              <form onSubmit={handleMfaSubmit} className="login-form">
                {stage === 'setup' && (
                  <div className="mfa-setup">
                    <p>Scansiona il codice con Google Authenticator, Microsoft Authenticator o un’app compatibile.</p>
                    {qrCodeDataUrl && <img className="mfa-qr-code" src={qrCodeDataUrl} alt="QR code per configurare il 2FA" />}
                    <p className="text-muted">Se non puoi scansionare il QR, inserisci manualmente questo segreto:</p>
                    <code className="mfa-manual-secret">{manualSecret}</code>
                  </div>
                )}
                {stage === 'verify' && (
                  <p className="mfa-verify-hint">Inserisci il codice a 6 cifre dell’app autenticatrice oppure un codice di recupero.</p>
                )}
                <div className="form-group">
                  <label htmlFor="mfa-code">Codice di verifica o recupero</label>
                  <input
                    id="mfa-code"
                    type="text"
                    inputMode="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    autoComplete="one-time-code"
                    placeholder="000000"
                    required
                    disabled={loading}
                  />
                </div>
                <button type="submit" className="login-button" disabled={loading}>
                  <span>{loading ? 'Verifica in corso...' : stage === 'setup' ? 'Attiva protezione' : 'Verifica e accedi'}</span>
                  {!loading && <ArrowRight size={18} />}
                </button>
                {passwordChangeRequired && (
                  <button
                    type="button"
                    className="login-back-button"
                    disabled={loading}
                    onClick={() => {
                      setError('');
                      setMfaCode('');
                      setStage('password');
                    }}
                  >
                    Modifica password
                  </button>
                )}
              </form>
            )}

            {stage === 'recovery' && (
              <div className="mfa-recovery">
                <p>Conserva questi codici in un luogo sicuro. Ognuno può essere usato una sola volta.</p>
                <ul>{recoveryCodes.map((code) => <li key={code}>{code}</li>)}</ul>
                <button type="button" className="login-button" onClick={handleRecoveryContinue}>
                  <span>Ho salvato i codici</span><ArrowRight size={18} />
                </button>
              </div>
            )}

            <div className="login-card-footer">
              <Lock size={14} />
              <span>Connessione sicura · Solo personale autorizzato</span>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

export default Login;
